import { globals } from 'config/globals';
import { Rect, Vector2 } from 'app/contracts/Geometry';
import { ParticleBehaviour } from '../ParticleBehaviour';
import { ParticleDepth } from './ParticleDepth';
import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import ParticleTargets from 'app/views/home/targets/ParticleTargets';
import ParticleCords from 'app/views/home/targets/ParticleCords';
import HomeColours from 'app/views/home/config/HomeColours';

const lerp = (x, y, a, ease) => {
	//1 == easeOutCubic
	if (ease == 1) {
		a = 1 - Math.pow(1 - a, 3);
	}

	//2 == easeInOutCubic
	if (ease == 2) {
		a = a < 0.5 ? 4 * a * a * a : 1 - Math.pow(-2 * a + 2, 3) / 2;
	}

	//3 easeInOutBack
	if (ease == 3) {
		const c1 = 1.70158;
		const c2 = c1 * 1.525;

		a =
			a < 0.5
				? (Math.pow(2 * a, 2) * ((c2 + 1) * 2 * a - c2)) / 2
				: (Math.pow(2 * a - 2, 2) * ((c2 + 1) * (a * 2 - 2) + c2) + 2) / 2;
	}

	//4 easeOutBack
	if (ease == 4) {
		const c1 = 1.70158;
		const c3 = c1 + 1;

		a = 1 + c3 * Math.pow(a - 1, 3) + c1 * Math.pow(a - 1, 2);
	}

	return x * (1 - a) + y * a;
};

export class ParticlePhysics extends ParticleBehaviour {
	public maxX: number;
	public maxY: number;

	private _vectorX: number;
	private _vectorY: number;

	private _gravity: number = 0.0002;
	private _boundary: Rect = { x: 0, y: 0.115, w: 0.09, h: 0.64 };
	private _depth: ParticleDepth;
	private _allowYBounce = true;
	private _rain: boolean = false;
	private _type: string = 'spaces';

	private _processHHitCounter = 0;

	private _target;
	private _targetId: string;
	private _onTargetReached: CallableFunction | null;
	private _targetXIncrement: number;
	private _targetYIncrement: number;

	private _xTargetDirection: number;
	private _yTargetDirection: number;

	private _allowReverseVector: boolean = false;

	private lerpStartX: number;
	private lerpEndX: number;
	private lerpStartY: number;
	private lerpEndY: number;
	private lerpEase: number = 1;
	private currentLerpFrames: number;
	private targetLerpFrames: number;
	private isLerping: boolean = false;

	private _processBoundaryTick: boolean = true;

	public set onTargetReached(f: CallableFunction | null) {
		this._onTargetReached = f;
	}

	public set boundary(v: Rect) {
		this._boundary = v;
	}

	public set depth(v: ParticleDepth) {
		this._depth = v;
	}

	public basic() {
		this._gravity = 0;
		this._vectorX = 0;
		this._vectorY = 0;
		this._allowYBounce = false;
	}

	public repell(x, y): void {
		this._vectorX += x;
		this._vectorY += y;
	}

	public get lerpTargetPosition(): Vector2 {
		return {
			x: this.lerpEndX,
			y: this.lerpEndY
		};
	}

	public static() {
		this._targetId = null;
		this._target = null;
		this.reset();
		this._type = 'systems';
	}

	public burst(): void {
		this._type == 'spaces';
		let directionX = Math.random() < 0.5 ? -1 : 1;
		let directionY = Math.random() < 0.5 ? -1 : 1;
		this._vectorX = (0.0001 + Math.random() * 0.0015) * directionX;
		this._vectorY = (0.0001 + Math.random() * 0.02) * directionY;
		this._allowYBounce = true;
		this._gravity = 0.0002;
		this._rain = false;
	}

	public burstUp(): void {
		this._type == 'spaces';

		let directionX = Math.random() < 0.5 ? -1 : 1;
		let directionY = -1;
		this._animatedProps.targetX = 0;
		this._animatedProps.targetY = 0;
		this._vectorX = (0.0001 + Math.random() * 0.004) * directionX;
		this._vectorY = (0.0001 + Math.random() * 0.02) * directionY;
		this._allowYBounce = true;

		let random = Math.random();
		this._gravity = 0.000055;
		if (random < 0.33) this._gravity = 0.000075;
		else if (random < 0.66) this._gravity = 0.000088;

		this._rain = false;
	}

	public rain(type: string = 'spaces'): void {
		this._type = type;
		this._animatedProps.targetX = 0;
		this._animatedProps.targetY = 0;
		this._vectorX = 0;
		this._vectorY = 0;
		this._rain = true;
		this._raindrop(false);
	}

	public reset() {
		this._vectorX = 0;
		this._vectorY = 0;
		this._gravity = 0;
	}

	public spawnAtTarget(targetId: string) {
		let target = ParticleTargets.getTarget(targetId);

		if (!target) {
			return;
		}

		this._particleProps.ref.animatedProps.targetX = 0;
		this._particleProps.ref.animatedProps.targetY = 0;

		this._particleProps.ref.animatedProps.targetScale = 0.25;
		this._particleProps.ref.animatedProps.targetAlpha = 1;

		this.active = true;


		let point = ParticleCords.screenPxToRelative({ x: target[0], y: target[1] }, this._sceneProps, 'systems');


		this.removeLerp();

		this._particleProps.ref.animatedProps.setVectorStyleMovement(false);
		this._particleProps.ref.position.set(point, true);


		this._particleProps.ref.animatedProps.setEase({
			x: 1,
			y: 1,
			scale: 1,
			alpha: 1
		});
	}

	public removeLerp() {
		this.currentLerpFrames = 0;
		this.targetLerpFrames = null;
		this.lerpEndX = null;
		this.lerpEndY = null;
		this.isLerping = false;
	}

	public lerpToPosition(pos: Vector2, timeMs: number = -1, ease = -1) {
		//Set target 60 FPS (Delta time will handle scaling):
		this.currentLerpFrames = 0;
		this.targetLerpFrames = timeMs / 16;

		//Set the final target in Abs px values:
		this.lerpStartX = this._particleProps.ref.position.x;
		this.lerpStartY = this._particleProps.ref.position.y;

		this.lerpEndX = pos.x;
		this.lerpEndY = pos.y;

		this.isLerping = true;

		if (ease == -1) {
			this.lerpEase = Math.random() < 0.5 ? 1 : 0;
		} else {
			this.lerpEase = ease;
		}
	}

	public startLerpToTarget(targetId: string, timeMs: number = -1) {
		let target = ParticleTargets.getTarget(targetId);

		if (!target) {
			console.log('NO TARGET ID', targetId);
			return;
		}

		let relCords = ParticleCords.screenPxToRelative({ x: target[0], y: target[1] }, this._sceneProps, 'systems');

		this.lerpToPosition(relCords, timeMs);
	}

	public target(id): void {
		this._targetId = id;
		this._raindrop(false);

		let target = ParticleTargets.getTarget(this._targetId);

		if (!target) {
			return;
		}

		this._target = ParticleCords.screenPxToRelative({ x: target[0], y: target[1] }, this._sceneProps, 'systems');

		this._xTargetDirection = this._target[0] < this._getRealX() ? -1 : 1;
		this._yTargetDirection = this._target[1] < this._particleProps.sprite.y ? -1 : 1;



		this._targetXIncrement = (0.00002 + Math.random() * 0.00006) * this._xTargetDirection;
		this._targetYIncrement = (0.00002 + Math.random() * 0.00008) * this._yTargetDirection;

		let speed = 0.000015 + Math.random() * 0.00004;
		this._targetXIncrement = 0.00005 * this._xTargetDirection;

		this._targetXIncrement = speed * this._xTargetDirection;

		this._targetYIncrement = speed * this._yTargetDirection * 5;

		this._gravity = 0;

		this._boundary = this._sceneProps.zonePositions.systemsPArea;
		this._boundary.x =
			this._sceneProps.zonePositions.systemsPArea.x + -1 * (this._sceneProps.zonePositions.systemsPArea.w / 2);

	}

	private _raindrop(resetY) {
		this._type = 'spaces';

		this._vectorY = 0;
		this._gravity = 0.00002 + Math.random() * 0.0004;

		let y = resetY ? this._boundary.y : this._particleProps.ref.position.y;

		this._vectorX = 0;

		if (this._type == 'spaces') {
			this._boundary = this._sceneProps.zonePositions.box;
			this._boundary.x = -1 * (this._sceneProps.zonePositions.box.w / 2);
		} else {
			this._boundary = this._sceneProps.zonePositions.systemsPArea;
			this._boundary.x =
				this._sceneProps.zonePositions.systemsPArea.x + -1 * (this._sceneProps.zonePositions.systemsPArea.w / 2);
			this._vectorX = 0.0005 + Math.random() * 0.001;
		}

		if (resetY) {
			this._particleProps.ref.position.set(
				{
					x: this._boundary.x + Math.random() * this._boundary.w,
					y: y
				},
				true
			);
		}
	}

	public leftDrop(type = 'screens'): void {
		this._type = type;

		this._processBoundaryTick = this._type == 'spaces';

		let m = Math.random() < 0.5 ? -1 : 1;
		this._vectorX = (0.0001 + Math.random() * 0.0003) * m;

		this._vectorY = 0;
		this._allowYBounce = false;
		this._gravity = 0.000063 + Math.random() * 0.000006;
		this._rain = false;
	}

	public drop(bounce = true, gravity = 0.0002): void {
		this._type = 'spaces';

		this._vectorY = 0;
		this._vectorX = 0;
		this._allowYBounce = bounce;
		this._gravity = 0.0002;
		this._rain = false;
	}

	public start(): void {
		this.active = true;
	}

	public tick(): void {
		if (this.isLerping) {
			this._processLerp();
			return;
		}

		this._processHitTest();

		if (this._type == 'spaces') {
			this._boundary = this._sceneProps.zonePositions.box;
			this._boundary.x = -1 * (this._sceneProps.zonePositions.box.w / 2);
		} else {
			this._boundary = this._sceneProps.zonePositions.systemsPArea;
			this._boundary.x = -1 * (this._sceneProps.zonePositions.systemsPArea.w / 2);
		}




		if (this._targetId) {
			this._processTarget();
		}

		let y = this._particleProps.ref.position.y + this._vectorY * (window as any).deltaTime;
		if (y < -0.2) y = -0.2;
		else if (y > 1) y = 1;
		this._particleProps.ref.position.set(
			{
				x: this._particleProps.ref.position.x + this._vectorX * (window as any).deltaTime,
				y: y
			},
			true
		);

		if (this._onTargetReached) {
		}



		this._vectorY += this._gravity * (window as any).deltaTime;

		if (this._vectorY > 0.006) {
			this._vectorY = 0.006;
		}

		if (this._vectorY < -0.006) {
			this._vectorY = -0.006;
		}



		if (this._vectorX > 0.0015) {
			this._vectorX = 0.0015;
		}

		if (this._vectorX < -0.0015) {
			this._vectorX = -0.0015;
		}




		if (this._processBoundaryTick) {
			this._processBoundary();
		}
	}

	private _cords() {
		return this._depth ? this._depth.orginalCords : this._particleProps.ref.position;
	}

	private _processBoundary(): void {
		this._boundary = this._sceneProps.zonePositions.box;
		this._boundary.x = -1 * (this._sceneProps.zonePositions.box.w / 2);

		let cords = this._cords();

		if (this._boundary) {
			if (cords.x > this._boundary.x + this._boundary.w && this._vectorX > 0) {
				this._vectorX *= -1;
			} else if (cords.x < this._boundary.x && this._vectorX < 0) {
				this._vectorX *= -1;
			}

			if (cords.y > this._boundary.y + this._boundary.h && this._vectorY > 0) {
				if (this._rain) {
					this._raindrop(true);
				} else if (this._allowYBounce) {
					this._vectorY *= -1;
				} else {
					this._vectorY = 0;
					this._gravity = 0;
				}
			} else if (cords.y < this._boundary.y && this._vectorY < 0) {
				this._vectorY *= -1;
			}
		}
	}

	private _processLerp() {
		//Increase by deltaTime to fix scaling issues:
		this.currentLerpFrames += (window as any).deltaTime;

		let timeRatio = Math.min(this.currentLerpFrames / this.targetLerpFrames, 1);

		let x = lerp(this.lerpStartX, this.lerpEndX, timeRatio, this.lerpEase);
		let y = lerp(this.lerpStartY, this.lerpEndY, timeRatio, this.lerpEase);

		//Set position using translate X/Y
		//Easing will be x:1, y:1  - so let the lerp equation sort the positing
		this._particleProps.ref.position.set(
			{
				x: x,
				y: y
			},
			false
		);

		//If time is all passed then end the target and
		if (timeRatio >= 1) {
			this.removeLerp();
			if (this._onTargetReached) {
				this._onTargetReached(this._particleProps.ref);
			}
		}
	}

	private _processTarget() {
		this._target = ParticleTargets.getTarget(this._targetId);

		if (!this._target) {
			return;
		}

		//Has target:

		let realX = this._getRealX();

		if (this._allowReverseVector) {
			this._vectorX += this._target[0] > realX ? this._targetXIncrement : -1 * this._targetXIncrement;
			this._vectorY +=
				this._target[1] > this._particleProps.sprite.y ? this._targetYIncrement : -1 * this._targetYIncrement;
			return;
		}

		this._vectorX += this._targetXIncrement * (window as any).deltaTime;
		this._vectorY += this._targetYIncrement * (window as any).deltaTime;

		if (
			(this._xTargetDirection == 1 && realX >= this._target[0]) ||
			(this._xTargetDirection == -1 && realX <= this._target[0])
		) {
			this._vectorX = 0;
		}

		if (
			(this._yTargetDirection == 1 && this._particleProps.sprite.y >= this._target[1]) ||
			(this._yTargetDirection == -1 && this._particleProps.sprite.y <= this._target[1])
		) {
			this._vectorY = 0;
		}

		if (this._onTargetReached && this._vectorX == 0 && this._vectorY == 0) {
			this._onTargetReached(this._particleProps.ref);
		}

		this._inSafeZone(realX);
	}

	private _inSafeZone(realX) {
	}

	private _processHitTest() {
		if (!ParticleHitTests.active) {
			return;
		}

		if (this._particleProps.sprite.visible === false) {
			return;
		}

		if (this._processHHitCounter > 0) {
			this._processHHitCounter -= (window as any).deltaTime;
			return;
		}


		let hasHit = ParticleHitTests.hasHit(Math.round(this._getRealX()), Math.round(this._particleProps.sprite.y));

		if (hasHit) {
			if (hasHit == 1) {
				this._vectorY *= -1;
				this._vectorY *= 0.3 + Math.random() * 0.2;
			} else {
				this._vectorX *= -1;
			}

			if (this._particleProps.ref.animatedProps.targetScale < 1.5) {
			}

			if (Math.abs(this._vectorY) > 0.001) {
				this._particleProps.ref.colour.set(HomeColours.getRandomColour(globals.bounceColourTarget));
			}

			this._processHHitCounter = 2;
		}
	}

	private _getRealX() {
		return this._sceneProps.mouse.particleContainersX['screens'] + this._particleProps.sprite.x;
	}
}
